import { Box, Flex, Icon as ChakraIcon, VStack } from '@chakra-ui/react';
import { OrderWithDeliveryOrders } from '@diamond/shared/types';
import { Icon, Text } from '@diamond/shared/ui';

import { OrderItemQuantityMetaMap } from './types';
import { formatOrderItemQuantity, resolveOrderItemUnit } from './utils';

type PendingItem = OrderWithDeliveryOrders['pending_items'][number];

export interface PendingItemsSectionProps {
  items: PendingItem[];
  orderItemMetaById?: OrderItemQuantityMetaMap;
}

function PendingHourglassIcon() {
  return (
    <ChakraIcon
      boxSize="20px"
      viewBox="0 0 20 20"
      fill="none"
      data-testid="pending-hourglass-icon"
    >
      <path
        d="M6.66683 16.6641H13.3335V14.1641C13.3335 13.2474 13.0071 12.4627 12.3543 11.8099C11.7016 11.1571 10.9168 10.8307 10.0002 10.8307C9.0835 10.8307 8.29877 11.1571 7.646 11.8099C6.99322 12.4627 6.66683 13.2474 6.66683 14.1641V16.6641ZM3.3335 18.3307V16.6641H5.00016V14.1641C5.00016 13.3168 5.19808 12.5217 5.59391 11.7786C5.98975 11.0356 6.54183 10.4418 7.25016 9.9974C6.54183 9.55295 5.98975 8.9592 5.59391 8.21615C5.19808 7.47309 5.00016 6.67795 5.00016 5.83073V3.33073H3.3335V1.66406H16.6668V3.33073H15.0002V5.83073C15.0002 6.67795 14.8022 7.47309 14.4064 8.21615C14.0106 8.9592 13.4585 9.55295 12.7502 9.9974C13.4585 10.4418 14.0106 11.0356 14.4064 11.7786C14.8022 12.5217 15.0002 13.3168 15.0002 14.1641V16.6641H16.6668V18.3307H3.3335Z"
        fill="#00003D"
      />
    </ChakraIcon>
  );
}

/**
 * Displays pending items that are not yet in any DO
 */
export function PendingItemsSection({
  items,
  orderItemMetaById,
}: PendingItemsSectionProps) {
  if (items.length === 0) {
    return null;
  }

  return (
    <Box
      py={2}
      data-testid="pending-items-section"
      bg="#F1F8FF"
      borderRadius="8px"
    >
      <Flex align="center" gap={1} mb={2} px={2} pt={1}>
        <PendingHourglassIcon />
        <Text fontSize="sm" fontWeight="medium" color="#00003D">
          Menunggu diproses
        </Text>
      </Flex>
      <VStack align="stretch" spacing={1} px={2} pb={1}>
        {items.map((item) => {
          const quantityMeta = orderItemMetaById?.[item.order_item_id];
          const pendingQuantity = formatOrderItemQuantity(
            item.pending_quantity,
            quantityMeta
          );
          const orderedQuantity = formatOrderItemQuantity(
            item.ordered_quantity,
            quantityMeta
          );
          const unitLabel = resolveOrderItemUnit(quantityMeta, item.sales_unit);

          return (
            <Box key={item.order_item_id} data-testid="pending-item">
              <Flex
                display={{ base: 'none', md: 'flex' }}
                justify="space-between"
                align="center"
                gap={3}
              >
                <Text
                  fontSize="xs"
                  color="#373F41"
                  noOfLines={1}
                  flex={1}
                  minW={0}
                >
                  {item.product_name}
                </Text>
                <Flex
                  align="baseline"
                  gap={1}
                  flexShrink={0}
                  data-testid={`pending-item-desktop-qty-${item.order_item_id}`}
                >
                  <Text
                    as="span"
                    fontFamily="'Libre Franklin', sans-serif"
                    fontWeight={400}
                    fontSize="14px"
                    lineHeight="20px"
                    color="#373F41"
                  >
                    {pendingQuantity}
                  </Text>
                  <Text
                    as="span"
                    fontFamily="'Libre Franklin', sans-serif"
                    fontWeight={400}
                    fontSize="10px"
                    lineHeight="100%"
                    verticalAlign="middle"
                    color="#373F41"
                    data-testid={`pending-item-desktop-qty-unit-${item.order_item_id}`}
                  >
                    {' '}
                    {unitLabel.toLowerCase()}
                  </Text>
                </Flex>
              </Flex>

              <Box display={{ base: 'block', md: 'none' }}>
                <Text fontSize="xs" color="#373F41" noOfLines={1}>
                  {item.product_name}
                </Text>
                <Text fontSize="xs" color="gray.400">
                  {pendingQuantity} dari {orderedQuantity} {unitLabel}
                </Text>
              </Box>
            </Box>
          );
        })}
      </VStack>
    </Box>
  );
}
